import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

import useGetClientFormsForSignature from "../hooks/useGetClientFormsForSignature";
import useToggleFormSkippedStatus from "../hooks/useToggleFormSkippedStatus";
import { useGetUserTask } from "hooks/api/user/useGetUserTask";
import useSaveIntakeProgress from "hooks/api/useSaveIntakeProgress";
import Form from "./Form";

type FormKey = string | number;

type ReadyForm = {
  pkey: FormKey;
  panda_doc_session_id?: string | null;
  panda_doc_document_id?: string | null;
};

const isReadyForm = (form: ReadyForm) =>
  Boolean(form.panda_doc_session_id && form.panda_doc_document_id);

function getIntakeStageForForm(formData) {
  const type = formData?.type?.replace("_SPANISH", "");
  switch (type) {
    case "BEHAVIORAL_HEALTH_SERVICES_CONSENT":
      return "behavioralHealthConsent";
    case "RESEARCH_CONSENT":
      return "researchConsent";
    case "PHI_RELEASE":
      if (formData.physician_type === "pediatrician") return "pediatricianROI";
      if (formData.physician_type === "diagnosing") return "diagnosingDoctorROI";
      return null;
    default:
      return null;
  }
}

/**
 * `FormsComponent` is a component that shows and handles pending forms for signature
 * @param {Object} props - Component properties
 * @param {string} props.clientId - Client ID
 * @param {Function} props.markAllFormsCompleted - Function to mark all forms as completed
 * @param {boolean} props.isIntakeFlow - Whether we're in intake flow (to track progress)
 * @returns {JSX.Element} Component to show and handle pending forms for signature
 */
const FormsComponent = ({ clientId, markAllFormsCompleted = () => {}, userId, isIntakeFlow }) => {
  const [currentFormId, setCurrentFormId] = useState<FormKey | null>(null);
  const [currentFormSnapshot, setCurrentFormSnapshot] = useState<ReadyForm | null>(null);
  const [completedForms, setCompletedForms] = useState<Record<FormKey, boolean>>({});
  const [showForms, setShowForms] = useState(true);
  const [fetchEnabled, setFetchEnabled] = useState(true);
  const { forms, isLoading, refetch } = useGetClientFormsForSignature(
    clientId,
    fetchEnabled,
    {},
    {
      queryKeySuffix: "signing-flow",
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  const { mutate, isLoading: skipIsLoading } = useToggleFormSkippedStatus(clientId);
  const { refetchTasks } = useGetUserTask(
    userId,
    clientId ? { client_id: clientId } : null
  );
  const { mutate: saveProgress } = useSaveIntakeProgress(clientId);
  const readyForms: ReadyForm[] = React.useMemo(
    () => forms.filter(isReadyForm),
    [forms]
  );

  const skipTriggeredRef = useRef(false);
  const initializedRef = useRef(false);
  const advancingFormRef = useRef<FormKey | null>(null);

  const trackFormProgress = useCallback(
    (formData) => {
      if (!isIntakeFlow) return;
      const stage = getIntakeStageForForm(formData);
      if (stage) {
        saveProgress({ stage, isStageCompleted: true });
      }
    },
    [isIntakeFlow, saveProgress]
  );

  useEffect(() => {
    if (!isLoading && readyForms.length > 0 && !currentFormId && !initializedRef.current) {
      const firstValidForm = readyForms[0];
      if (firstValidForm) {
        setCurrentFormId(firstValidForm.pkey);
        setCurrentFormSnapshot(firstValidForm);
        initializedRef.current = true;
      }
    }
  }, [readyForms, isLoading, currentFormId]);

  useEffect(() => {
    if (!isLoading) {
      setFetchEnabled(false);
    }
  }, [isLoading]);

  const handleSkip = (formId) => {
    skipTriggeredRef.current = true;
    const currentIndex = readyForms.findIndex((form) => form.pkey === formId);
    const currentForm =
      currentIndex >= 0 ? readyForms[currentIndex] : currentFormSnapshot;

    mutate(
      { formId },
      {
        onSuccess: async () => {
          trackFormProgress(currentForm);
          setFetchEnabled(true);
          await refetchTasks();
          const nextForm =
            currentIndex >= 0 ? readyForms[currentIndex + 1] : readyForms[0];
          if (nextForm) {
            setCurrentFormId(nextForm.pkey);
            setCurrentFormSnapshot(nextForm);
          }
        },
      }
    );
  };

  const handleCancel = () => {
    setCurrentFormId(null);
    setCurrentFormSnapshot(null);
    setShowForms(false);
  };

  const handleNext = async (currentPkey: FormKey) => {
    if (advancingFormRef.current === currentPkey) {
      return;
    }
    advancingFormRef.current = currentPkey;

    try {
      const currentIndex = readyForms.findIndex((form) => form.pkey === currentPkey);
      const currentForm =
        currentIndex >= 0 ? readyForms[currentIndex] : currentFormSnapshot;
      trackFormProgress(currentForm);

      const nextForm =
        currentIndex >= 0 ? readyForms[currentIndex + 1] : readyForms[0];
      if (nextForm?.panda_doc_session_id && nextForm?.panda_doc_document_id) {
        setCurrentFormId(nextForm.pkey);
        setCurrentFormSnapshot(nextForm);
        return;
      }

      const refreshResult = await refetch();
      if (refreshResult.isError) {
        console.error("Error advancing form flow: form refresh failed");
        return;
      }
      const refreshedForms = refreshResult.data;
      const refreshedFormList: ReadyForm[] = Array.isArray(refreshedForms)
        ? refreshedForms.filter(isReadyForm)
        : readyForms;
      const refreshedCurrentIndex = refreshedFormList.findIndex(
        (form) => form.pkey === currentPkey
      );
      const remainingForms =
        refreshedCurrentIndex >= 0
          ? refreshedFormList.slice(refreshedCurrentIndex + 1)
          : refreshedFormList;
      const refreshedNextForm = remainingForms.find(
        (form) =>
          form.pkey !== currentPkey &&
          !completedForms[form.pkey] &&
          form.panda_doc_session_id &&
          form.panda_doc_document_id
      );
      if (refreshedNextForm) {
        setCurrentFormId(refreshedNextForm.pkey);
        setCurrentFormSnapshot(refreshedNextForm);
        return;
      }

      markAllFormsCompleted();

      setTimeout(async () => {
        try {
          await refetchTasks();
        } catch (error) {
          console.error("Error refetching tasks:", error);
        }
      }, 3000);
    } catch (error) {
      console.error("Error advancing form flow:", error);
    } finally {
      if (advancingFormRef.current === currentPkey) {
        advancingFormRef.current = null;
      }
    }
  };

  const handleBack = (currentPkey) => {
    const currentIndex = readyForms.findIndex((form) => form.pkey === currentPkey);
    if (currentIndex > 0) {
      const previousForm = readyForms[currentIndex - 1];
      setCurrentFormId(previousForm.pkey);
      setCurrentFormSnapshot(previousForm);
    }
  };

  const setFormComplete = useCallback((pkey: FormKey) => {
    setCompletedForms((prev) => ({ ...prev, [pkey]: true }));
  }, []);

  const currentForm =
    readyForms.find((form) => form.pkey === currentFormId) ??
    currentFormSnapshot;

  if (!showForms || !currentForm || isLoading) {
    return null;
  }

  return createPortal(
    <Form
      key={currentForm.pkey}
      formData={currentForm}
      handleNext={() => handleNext(currentForm.pkey)}
      handleBack={() => handleBack(currentForm.pkey)}
      index={Math.max(
        readyForms.findIndex((form) => form.pkey === currentForm.pkey),
        0
      )}
      completed={!!completedForms[currentForm.pkey]}
      setFormComplete={() => setFormComplete(currentForm.pkey)}
      handleSkip={handleSkip}
      handleCancel={handleCancel}
      isLoading={isLoading}
      skipIsLoading={skipIsLoading}
    />,
    document.body
  );
};

export default FormsComponent;
